/**
 * Pane 3 — Your order (round-5 photo-centric rework). The pane lists ORDERED
 * PHOTOS, not size lines:
 *   - photos sharing an IDENTICAL print config collapse into ONE stacked group
 *     row (overlapping thumbs, "59 photos · 4×6 (1) Gloss each", group total,
 *     ✕ remove-with-confirm) — a 200-photo wizard order stays a single row;
 *   - photos with unique configs get individual rows (40px thumb, compact
 *     print summary, photo total, ✎ edit / + add size / ✕ remove; tap a
 *     multi-print row to expand per-print edit/remove).
 * Below the list: the compact by-size totals summary, the bulk-pricing nudge,
 * and the footer with the attention link + subtotal. Continue lives in the
 * screen's sticky bottom bar (round 5 — no duplicate button here).
 *
 * ROUND 7:
 *  - QUANTITY on rows: single-print rows get a direct stepper; multi-photo
 *    group rows open the per-photo quantity editor (PhotoQtyDialog) so the
 *    customer says which photo gets more prints; expanded per-print lines get
 *    their own steppers.
 * ROUND 9: single-photo rows with SEVERAL distinct prints get a qty control
 *    too — it opens the per-print editor (PrintQtyDialog) so the customer
 *    picks WHICH print to add or remove instead of guessing.
 *  - RECONCILIATION: group rows count only PRICED photos in the headline and
 *    carry an explicit amber "+N need review · not counted yet" line for
 *    blocked prints, so price × count always adds up for the customer.
 */

import { bulkState, parseSizeId, type PricingData } from '@rpl/pricing';
import { useMemo, useState } from 'react';
import { formatMoney } from '../../lib/format';
import { flaggedForReview, pricedPrints, sizeShort, sizeWithUnit } from '../../lib/sizePaper';
import { toPrintSelections, useStore, useTotals } from '../../store/store';
import { keyOfGroup, summaryLabel } from '../cart/cartModel';
import { PhotoQtyDialog, QtyOpenButton } from '../cart/PhotoQtyDialog';
import { PrintQtyDialog } from '../cart/PrintQtyDialog';
import { photoConfigSummary, photoOrderRows, rowBlockedLabel, rowLabel, type PhotoOrderRow } from './photoOrder';
import { ModalShell } from './ModalShell';
import { QtyStepper } from './QtyStepper';

export interface CartPaneProps {
  pricing: PricingData;
  /** Open the print editor (edit mode) for a print id. */
  onOpenCrop: (printId: string) => void;
  /** Open the per-image editor in "add a print" mode for a photo. */
  onAddPrint: (photoId: string) => void;
  /** Open the Review-crops modal (attention line). */
  onReview: () => void;
  /** Bumps on each Add to briefly pulse the header ("saved to order"). */
  pulseKey: number;
}

const STACK_MAX = 4;

export function CartPane({ pricing, onOpenCrop, onAddPrint, onReview, pulseKey }: CartPaneProps) {
  const prints = useStore((s) => s.prints);
  const photos = useStore((s) => s.photos);
  const removePrint = useStore((s) => s.removePrint);
  const updatePrint = useStore((s) => s.updatePrint);
  const totals = useTotals();
  const flaggedCount = useMemo(() => flaggedForReview(prints).length, [prints]);

  const rows = useMemo(() => photoOrderRows(prints), [prints]);
  const photoById = useMemo(() => {
    const map = new Map<string, { previewUrl: string | null; filename: string }>();
    for (const p of photos) map.set(p.id, { previewUrl: p.previewUrl, filename: p.filename });
    return map;
  }, [photos]);

  // Expansion tracks the PHOTO id (stable) — row keys embed the config
  // signature, which changes when a per-print stepper edits qty (round 7).
  const [expanded, setExpanded] = useState<string | null>(null);
  const [confirmRow, setConfirmRow] = useState<PhotoOrderRow | null>(null);
  // Per-photo quantity editor target (multi-photo group rows).
  const [qtyRow, setQtyRow] = useState<PhotoOrderRow | null>(null);
  // Per-print quantity editor target (round 9: one photo, several prints).
  const [printQtyRow, setPrintQtyRow] = useState<PhotoOrderRow | null>(null);

  const bulkStates = useMemo(
    () => bulkState(pricing, toPrintSelections(pricedPrints(prints))),
    [pricing, prints],
  );
  const active = bulkStates.find((b) => b.active);
  const candidate = bulkStates
    .filter((b) => !b.active && b.total > 0)
    .sort((a, b) => b.total / b.threshold - a.total / a.threshold)[0];
  const nudge = active ?? candidate ?? null;

  const sizeLines = totals?.groups ?? [];

  const removeRow = (row: PhotoOrderRow) => {
    for (const p of row.prints) removePrint(p.id);
    setConfirmRow(null);
  };

  const thumb = (photoId: string, cls: string) => {
    const ph = photoById.get(photoId);
    return ph?.previewUrl ? (
      <img key={photoId} className={cls} src={ph.previewUrl} alt="" loading="lazy" title={ph.filename} />
    ) : (
      <span key={photoId} className={`${cls} oprow__thumb--ph`} />
    );
  };

  /**
   * Thumb as an EDIT target (Clarity round 4, 2026-08-12). People click the
   * picture — it was the most-clicked dead element in the pane — so the thumb
   * itself opens the editor, with a ✎ that fades in on hover/focus.
   */
  const thumbButton = (photoId: string, printId: string, cls: string) => (
    <button
      type="button"
      className="oprow__thumbbtn"
      aria-label="Edit this photo's print"
      title="Edit print"
      onClick={(e) => {
        e.stopPropagation();
        onOpenCrop(printId);
      }}
    >
      {thumb(photoId, cls)}
      <span className="oprow__thumbedit" aria-hidden>
        ✎
      </span>
    </button>
  );

  return (
    <section className="pane pane--cart">
      <header key={pulseKey} className={`pane__head${pulseKey > 0 ? ' cartpane__head--pulse' : ''}`}>
        <h2 className="pane__title">Your order</h2>
        {totals && <span className="pane__sub">{totals.printCount} in cart</span>}
      </header>

      <div className="cartpane__scroll">
        {rows.length === 0 ? (
          <div className="pane__empty">Nothing yet. Pick photos, choose a size, then add to cart.</div>
        ) : (
          rows.map((row) => {
            const group = row.photoIds.length > 1;
            const firstId = row.photoIds[0]!;
            const multi = !group && row.configPrints.length > 1;
            const open = expanded === firstId;
            const label = rowLabel(pricing, row);
            const blockedLine = rowBlockedLabel(row);
            const reviewCount = row.attention - row.blocked; // priced, still to confirm
            const groupQty = row.prints.reduce((a, p) => a + p.qty, 0);
            const singlePrint = !group && !multi ? row.configPrints[0]! : null;
            // Rows that open in place rather than jumping to the editor.
            const expandable = multi || group;
            return (
              <div key={row.key} className={`oprow${group ? ' oprow--group' : ''}`}>
                <div
                  className="oprow__main"
                  // Multi-print AND multi-photo GROUP rows expand; single-print
                  // rows open the editor. (Clarity round 3 made the label
                  // clickable for single/multi; round 4, 2026-08-12, found
                  // group rows were still inert — every remaining dead click in
                  // this pane landed on a group row's stack or label, because a
                  // group row had no row action and no ✎ at all.)
                  role={expandable || singlePrint ? 'button' : undefined}
                  aria-expanded={expandable ? open : undefined}
                  aria-label={
                    singlePrint ? `Edit ${label}` : expandable ? `${open ? 'Hide' : 'Show'} ${label}` : undefined
                  }
                  tabIndex={expandable || singlePrint ? 0 : undefined}
                  onClick={
                    expandable
                      ? () => setExpanded(open ? null : firstId)
                      : singlePrint
                        ? () => onOpenCrop(singlePrint.id)
                        : undefined
                  }
                  onKeyDown={
                    expandable || singlePrint
                      ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            if (expandable) setExpanded(open ? null : firstId);
                            else if (singlePrint) onOpenCrop(singlePrint.id);
                          }
                        }
                      : undefined
                  }
                >
                  {group ? (
                    // The stack stays decorative: overlapping 32px thumbs are
                    // too imprecise to aim at. Expanding gives every photo its
                    // own full-size, individually editable thumb instead.
                    <span className="oprow__stack" aria-hidden>
                      {row.photoIds.slice(0, STACK_MAX).map((id) => thumb(id, 'oprow__thumb'))}
                      {row.photoIds.length > STACK_MAX && (
                        <span className="oprow__more">+{row.photoIds.length - STACK_MAX}</span>
                      )}
                    </span>
                  ) : singlePrint ? (
                    thumbButton(firstId, singlePrint.id, 'oprow__thumb oprow__thumb--single')
                  ) : (
                    thumb(firstId, 'oprow__thumb oprow__thumb--single')
                  )}
                  <span className="oprow__info">
                    <span className="oprow__label">{label}</span>
                    {blockedLine && (
                      <span className="oprow__attn oprow__attn--blocked">{blockedLine}</span>
                    )}
                    {reviewCount > 0 && (
                      <span className="oprow__attn">{reviewCount} to review</span>
                    )}
                  </span>
                  <span
                    className="oprow__qtybox"
                    // The row surface now reacts (expand for multi, edit for
                    // single) — a qty tap must not bubble to it.
                    onClick={multi || singlePrint ? (e) => e.stopPropagation() : undefined}
                  >
                    {group ? (
                      <QtyOpenButton qty={groupQty} label={label} onOpen={() => setQtyRow(row)} />
                    ) : multi ? (
                      <QtyOpenButton
                        qty={groupQty}
                        label={label}
                        title="Choose which print to add or remove"
                        ariaPrefix="Change quantities per print"
                        onOpen={() => setPrintQtyRow(row)}
                      />
                    ) : (
                      <QtyStepper
                        variant="light"
                        ariaLabel={`Quantity, ${label}`}
                        value={singlePrint!.qty}
                        onChange={(qty) => updatePrint(singlePrint!.id, { qty })}
                      />
                    )}
                  </span>
                  <span className="oprow__price">{formatMoney(row.total)}</span>
                  <span className="oprow__actions">
                    {/* Affordance (round 4): expandable rows LOOK expandable.
                        Without it the row read as inert, so people hunted for
                        something clickable — usually the photo. */}
                    {expandable && (
                      <span className={`oprow__chev${open ? ' oprow__chev--open' : ''}`} aria-hidden>
                        ⌄
                      </span>
                    )}
                    {!group && (
                      <>
                        <button
                          type="button"
                          className="oprow__btn"
                          aria-label="Edit this photo's print"
                          title="Edit print"
                          onClick={(e) => {
                            e.stopPropagation();
                            onOpenCrop(row.configPrints[0]!.id);
                          }}
                        >
                          ✎
                        </button>
                        <button
                          type="button"
                          className="oprow__btn"
                          aria-label="Add another size for this photo"
                          title="Add another size"
                          onClick={(e) => {
                            e.stopPropagation();
                            onAddPrint(firstId);
                          }}
                        >
                          +
                        </button>
                      </>
                    )}
                    <button
                      type="button"
                      className="oprow__btn oprow__btn--x"
                      aria-label={group ? `Remove prints for ${row.photoIds.length} photos` : 'Remove this photo from the order'}
                      title={
                        group
                          ? 'Remove these prints. To change one photo, edit it in the photo grid.'
                          : 'Remove all prints for this photo'
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        setConfirmRow(row);
                      }}
                    >
                      ✕
                    </button>
                  </span>
                </div>
                {/* GROUP rows expand to one line PER PHOTO — thumb (editable),
                    its own quantity, edit and remove. Before this, a group row
                    was the only row with no action at all: no row click, no ✎,
                    just ✕, and the "edit it in the photo grid" instruction was
                    hidden in a title attribute nobody reads. */}
                {group && open && (
                  <div className="oprow__detail oprow__detail--photos">
                    {row.photoIds.map((photoId) => {
                      const photoPrints = row.prints.filter((p) => p.photoId === photoId);
                      const first = photoPrints[0];
                      if (!first) return null;
                      const ph = photoById.get(photoId);
                      const onePrint = photoPrints.length === 1;
                      return (
                        <div key={photoId} className="oprow__photoline">
                          {thumbButton(photoId, first.id, 'oprow__thumb oprow__thumb--mini')}
                          <span className="oprow__photolabel">
                            {ph?.filename ?? photoConfigSummary(pricing, photoPrints)}
                          </span>
                          {onePrint ? (
                            <QtyStepper
                              variant="light"
                              ariaLabel={`Quantity, ${ph?.filename ?? label}`}
                              value={first.qty}
                              onChange={(qty) => updatePrint(first.id, { qty })}
                            />
                          ) : (
                            <span className="oprow__photoqty">×{photoPrints.reduce((a, p) => a + p.qty, 0)}</span>
                          )}
                          <button
                            type="button"
                            className="oprow__btn"
                            aria-label={`Edit ${ph?.filename ?? 'this photo'}`}
                            title="Edit print"
                            onClick={() => onOpenCrop(first.id)}
                          >
                            ✎
                          </button>
                          <button
                            type="button"
                            className="oprow__btn oprow__btn--x"
                            aria-label={`Remove ${ph?.filename ?? 'this photo'} from the order`}
                            title="Remove this photo"
                            onClick={() => {
                              for (const p of photoPrints) removePrint(p.id);
                            }}
                          >
                            ✕
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
                {multi && open && (
                  <div className="oprow__detail">
                    {row.configPrints.map((p) => (
                      <div key={p.id} className="oprow__printline">
                        <span className="oprow__printlabel">{photoConfigSummary(pricing, [p])}</span>
                        <QtyStepper
                          variant="light"
                          ariaLabel={`Quantity, ${photoConfigSummary(pricing, [p])}`}
                          value={p.qty}
                          onChange={(qty) => updatePrint(p.id, { qty })}
                        />
                        <button
                          type="button"
                          className="oprow__btn"
                          aria-label="Edit this print"
                          title="Edit print"
                          onClick={() => onOpenCrop(p.id)}
                        >
                          ✎
                        </button>
                        <button
                          type="button"
                          className="oprow__btn oprow__btn--x"
                          aria-label="Remove this print"
                          title="Remove print"
                          onClick={() => removePrint(p.id)}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })
        )}

        {sizeLines.length > 0 && (
          <div className="cartpane__bysize">
            <div className="cartpane__bysize-head">By size</div>
            {sizeLines.map((g) => (
              <div key={keyOfGroup(g)} className="cartpane__bysize-row">
                <span className="cartpane__bysize-label">
                  {summaryLabel(g)}
                  {g.bulkApplied && <span className="opline__bulk"> · bulk</span>}
                </span>
                <span className="cartpane__bysize-price">{formatMoney(g.lineTotal)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {nudge && (
        <div className="cartpane__nudge">
          <div className="cartpane__nudge-bar" aria-hidden>
            <span
              className="cartpane__nudge-fill"
              style={{ width: `${Math.min(100, (nudge.total / nudge.threshold) * 100)}%` }}
            />
          </div>
          <div className="cartpane__nudge-copy">
            {nudge.active ? (
              <>
                Bulk price applied. {sizeWithUnit(parseSizeId(nudge.sizeId).size)} is now{' '}
                <strong>{formatMoney(nudge.bulkPrice)}</strong> each.
              </>
            ) : (
              <>
                Add {nudge.remaining} more {sizeWithUnit(parseSizeId(nudge.sizeId).size)} to drop to{' '}
                <strong>{formatMoney(nudge.bulkPrice)}</strong> each.
              </>
            )}
          </div>
        </div>
      )}

      <div className="cartpane__foot">
        {flaggedCount > 0 && (
          <button type="button" className="cartpane__attn" onClick={onReview}>
            {flaggedCount} print{flaggedCount === 1 ? '' : 's'} need your attention. Review →
          </button>
        )}
        <div className="cartpane__total">
          <span className="cartpane__total-label">Subtotal</span>
          <span className="cartpane__total-value">{formatMoney(totals?.subtotal ?? 0)}</span>
        </div>
      </div>

      {qtyRow && (
        <PhotoQtyDialog
          members={qtyRow.prints}
          photosById={photoById}
          title={rowLabel(pricing, qtyRow)}
          onClose={() => setQtyRow(null)}
        />
      )}

      {printQtyRow && (
        <PrintQtyDialog
          pricing={pricing}
          members={printQtyRow.configPrints}
          photo={photoById.get(printQtyRow.photoIds[0]!)}
          onClose={() => setPrintQtyRow(null)}
        />
      )}

      {confirmRow && (
        <ModalShell onClose={() => setConfirmRow(null)} width={420} label="Remove prints" className="confirm">
          <div className="confirm__body">
            <h3 className="confirm__title">
              {confirmRow.photoIds.length > 1
                ? `Remove prints for ${confirmRow.photoIds.length} photos?`
                : 'Remove this photo from the order?'}
            </h3>
            <p className="confirm__text">
              The photo{confirmRow.photoIds.length > 1 ? 's stay' : ' stays'} in your library, ready to set
              up again.
            </p>
            <div className="confirm__actions">
              <button type="button" className="btn btn--secondary" onClick={() => setConfirmRow(null)}>
                Keep
              </button>
              <button
                type="button"
                className="btn btn--primary confirm__danger"
                onClick={() => removeRow(confirmRow)}
              >
                Remove
              </button>
            </div>
          </div>
        </ModalShell>
      )}
    </section>
  );
}
